import {useContext, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useNavigate} from "react-router-dom";
import {faBell, faClone, faExclamation, faLock, faUnlock} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {useSocket} from "../../contexts/socket.js";
import {AuthContext} from "../../contexts/auth.js";

// Hooks
import useApi from "../../hooks/useApi.js";
import {useToast} from "../../contexts/toast.js";

// Utils
import {exists, slugify} from "../../utils/helpers.js";

// Components
import Modal from "../../components/Modal.js";
import FacilityArchiveDangerZone from "../facility/FacilityArchiveDangerZone.js";

// Style
import {pad, shadow} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {Heading, Text, Button, ButtonLoader, Inline} from "../../style/components/general.js";

// Socket Constants
import Room, {LOCK_FACILITY} from "../general/Room.js";

const ModalFacilitySettings = ({visible, setVisible, facility, reloadFacilities, lock}) => {
  const {id, name, types, details, builder} = facility;

  const navigate = useNavigate();

  const socket = useSocket();

  const {addToast} = useToast();

  const {currentUser, roleCanAccessResource} = useContext(AuthContext);

  const {api: facilityApi} = useApi("facilities");

  const [cloneLoading, setCloneLoading] = useState(false);

  const [active, setActive] = useState([]);
  const [showUsers, setShowUsers] = useState(false);

  const cloneFacility = () => {
    setCloneLoading(true);
    facilityApi
      .callPost({
        types: types.map(({id: typeId}) => typeId),
        name,
        details,
        builder,
        duplicate: true
      })
      .then(() => {
        reloadFacilities();
        setVisible(false);
        setCloneLoading(false);
      });
  };

  const closeAfterChange = () => {
    reloadFacilities();
    setVisible(false);
  };

  return (
    <Modal visible={visible} setVisible={setVisible}>
      <Room name={slugify(facility.name)} setActive={setActive} />
      <Menu fullWidth>
        <ModalTitle>Manage {name}</ModalTitle>
        <Inline>
          {active?.users?.filter(user => currentUser.publicId !== user.publicId).length > 0 && (
            <ActiveUsers type="button" onClick={() => setShowUsers(prev => !prev)}>
              <span>
                &nbsp;&nbsp;
                {active.users.filter(user => currentUser.publicId !== user.publicId).length}
                &nbsp;Users
              </span>
              {showUsers && currentUser && (
                <ul>
                  {active.users
                    .filter(user => currentUser.publicId !== user.publicId)
                    .map(user => (
                      <li key={user.publicId}>{user.name}</li>
                    ))}
                </ul>
              )}
            </ActiveUsers>
          )}
          {currentUser.type.name === "super" && (
            <LockButton
              type="button"
              onClick={() => {
                if (exists(lock) && Number(lock) !== id)
                  addToast("A facility is already locked. Please unlock before locking another.");
                else socket.emit(LOCK_FACILITY, facility.slug);
              }}>
              <FontAwesomeIcon icon={exists(lock) && id === Number(lock) ? faUnlock : faLock} />
              &nbsp;
              {id === Number(lock) ? "Unlock" : "Lock"}
            </LockButton>
          )}
        </Inline>
      </Menu>

      {!facility.isDeleted && (
        <>
          {roleCanAccessResource("notifications", "edit") && (
            <>
              <>
                <Text>Configure notifications.&nbsp;</Text>
                {!facility.hasNotifications && (
                  <StyledSpanQuiet>
                    <Circle>
                      <FontAwesomeIcon icon={faExclamation} />
                    </Circle>
                    &nbsp;&nbsp;
                    <Text>Notifications have not been configured for this facility.</Text>
                  </StyledSpanQuiet>
                )}
              </>
              <Option
                type="button"
                onClick={() => navigate(`/facilities/${facility.slug}/notifications`)}>
                <FontAwesomeIcon icon={faBell} />
                Go to Notifications
              </Option>
            </>
          )}
          {roleCanAccessResource("facility", "create") && (
            <>
              <Text>Use as template.</Text>

              <Option
                type="button"
                title="Clone"
                onClick={cloneFacility}
                loading={cloneLoading ? 1 : 0}>
                <FontAwesomeIcon icon={faClone} />
                Clone {cloneLoading ? <ButtonLoader /> : ""}
              </Option>
            </>
          )}
        </>
      )}

      <FacilityArchiveDangerZone
        facility={facility}
        onRestored={closeAfterChange}
        onArchived={closeAfterChange}
        onDeleted={closeAfterChange}
      />
    </Modal>
  );
};

ModalFacilitySettings.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  reloadFacilities: PropTypes.func.isRequired,
  lock: PropTypes.string
};

// Style Overrides
const Menu = styled(Inline)`
  justify-content: space-between;
`;

const ModalTitle = styled(Heading)`
  margin: ${pad}px 0;
`;

const Option = styled(Button)`
  margin-bottom: ${pad / 2}px;
  svg {
    margin-right: ${pad}px;
  }
`;

const Circle = styled.span`
  background: ${({theme}) => theme.error};
  border-radius: 50%;
  padding: ${pad / 2}px;
  padding-left: 6px;
  text-align: center;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;

  svg {
    fill: ${({theme}) => theme.tertiary};
  }
`;

const StyledSpanQuiet = styled.span`
  ${flex("row", "nowrap", "start", "center")}
  ${voice.quiet}

  margin-bottom: ${pad / 2}px;

  p {
    ${voice.quiet}
  }
`;

const Banner = styled(Button)`
  ${voice.quiet};
  height: min-content;
  padding: ${pad / 4}px ${pad / 2}px;
  border-radius: 10px;
  text-transform: uppercase;
  font-weight: bold;
  text-align: left;
  background-color: ${({theme}) => theme.primary};
  margin-left: ${pad}px;
  box-shadow: ${shadow};

  li {
    ${voice.quiet};
  }
`;

const ActiveUsers = styled(Banner)`
  background-color: ${({theme}) => theme.primary};
  width: max-content;

  span {
    margin: 0 ${pad / 4}px;
    white-space: nowrap;
    display: flex;

    :before {
      content: "";
      display: inline-block;
      width: 8px;
      height: 8px;
      background: ${({theme}) => theme.tertiary};
      border-radius: 50%;
      margin: 2px auto 0;
    }
  }
`;

const LockButton = styled(Button)`
  white-space: nowrap;
`;

export default ModalFacilitySettings;
